import { TooltipContentProps } from 'recharts';
import styled from 'styled-components';

import { typographyCss } from '@creditornot/cb-ingredients';
import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import {
  PerformanceMetric as PerformanceMetricType,
  PerformanceOverviewPeriod,
} from 'common/api-types/recommendations';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { formatDate } from 'common/utils/dates/dateUtils';

import { getMetricValueUnit } from '../utils';

const Root = styled(Stack)`
  ${typographyCss.footnote};

  background-color: ${color.bgSurfaceInverse};
  min-width: fit-content;
  color: ${color.textInverse};
  padding: ${dimension.spaceRegularMedium};
`;

const DateFrom = styled.div`
  white-space: nowrap;
`;

const Symbol = styled.span`
  color: ${color.textInverse};
`;

type Props = Pick<TooltipContentProps<number, string>, 'active' | 'payload' | 'coordinate'> & {
  metricType: PerformanceMetricType;
  timePeriod: PerformanceOverviewPeriod;
};

export const PerformanceMetricTooltip = ({ active, payload, metricType, timePeriod }: Props) => {
  const f = useFormatMessage();
  const locale = useUserLocale();
  const { leftUnit, rightUnit } = getMetricValueUnit()[metricType];

  if (active && payload && payload.length) {
    const { value, date } = payload[0].payload;
    const formattedDate =
      timePeriod === 'LAST_WEEK'
        ? formatDate(locale, date)
        : formatDate(locale, date, { month: 'short', year: 'numeric' });

    return (
      <Root gap="narrowSmall">
        <DateFrom>
          {timePeriod === 'LAST_WEEK'
            ? f('selection_recommendations.performance_overview_week_of', {
                date: formattedDate,
              })
            : formattedDate}
        </DateFrom>
        <div>
          {leftUnit && <Symbol>{leftUnit}</Symbol>}
          {value}
          {rightUnit && <Symbol>{rightUnit}</Symbol>}
        </div>
      </Root>
    );
  }

  return null;
};
